import { defineSearch, param, type RawSearch } from '@/router/search';
import type { FilterKey, GroupBy, RevenueMode, RevenueType } from './types';

export const GROUP_BYS = ['media_source', 'campaign', 'adset', 'creative', 'country', 'bundle_id'] as const satisfies readonly GroupBy[];
export const REVENUE_MODES = ['gross', 'net'] as const satisfies readonly RevenueMode[];
export const REVENUE_TYPES = ['in_app_revenue', 'ad_revenue'] as const satisfies readonly RevenueType[];
export const FILTER_KEYS = ['media_source', 'campaign', 'adset', 'creative', 'country', 'bundle_id'] as const satisfies readonly FilterKey[];

/**
 * /predictive-analytics URL state: the whole forecast view, so a link or a
 * reload shows the same forecast (apps/web kept it in memory only — the
 * parity QUIRK). Keys are the API's own parameter names. Anything absent
 * takes the dashboard's default: the first dashboard, D30 (or the furthest
 * horizon below it), gross, by media source, both revenue sources, and the
 * last 30 install dates — so an untouched view keeps rolling forward.
 */
export const parseForecastSearch = (raw: RawSearch) => {
  const dates = param.dateRange(raw, 'install_date_from', 'install_date_to');
  return {
    dashboard: param.string(raw, 'dashboard'),
    predict_day: param.number(raw, 'predict_day', { min: 0, max: 365, integer: true }),
    revenue_mode: param.enumValue(raw, 'revenue_mode', REVENUE_MODES),
    group_by: param.enumValue(raw, 'group_by', GROUP_BYS),
    install_date_from: dates?.from,
    install_date_to: dates?.to,
    media_source: param.list(raw, 'media_source'),
    campaign: param.list(raw, 'campaign'),
    adset: param.list(raw, 'adset'),
    creative: param.list(raw, 'creative'),
    country: param.list(raw, 'country'),
    bundle_id: param.list(raw, 'bundle_id'),
    /** [] means both. */
    revenue_type: param.enumList(raw, 'revenue_type', REVENUE_TYPES),
  };
};

export type ForecastSearch = ReturnType<typeof parseForecastSearch>;

export const forecastSearch = defineSearch(parseForecastSearch);
